import { Badge, Button, Inline, Legend, Stack, StatusDot, t, Text, Tooltip } from '@kanbios/ui';
import { type Enums, useSession } from '../lib/session';
import { type DotSpec, HealthDot, HealthLegend, healthColumn } from './StateColumn';

/** State labels and tones come from /enums: the front never copies them (ADR-003, G-05). */
function stateMeta(enums: Enums, state: string) {
  return enums.states.find(s => s.value === state) ?? { value: state, label: state, tone: 'neutral' as const };
}
function useStateMeta(state: string) {
  const { enums } = useSession();
  return stateMeta(enums, state);
}

/** The dot of a server state; `note` (Unknown) and `reason` (its first cause) go into the tooltip. */
export function serverStateSpec(enums: Enums, state: string, reason?: string | null, note?: string | null): DotSpec {
  const meta = stateMeta(enums, state);
  return { tone: meta.tone, label: meta.label, description: [note, reason].filter(Boolean).join(' · ') || undefined };
}

/** D53: « Pas en production » — nothing runs in production, no state to aggregate (SPEC-005). A halo: shape apart from Maintenance (info) and Inconnu (ring). */
export const noProductionSpec = (): DotSpec => ({ tone: 'neutral', variant: 'halo', label: t('inventory.noProd'), description: t('inventory.noProdHelp') });

/** A server state as a dot: colour only, the state's name and cause in its tooltip (DSM StatusDot). */
export function ServerStateDot({ state, reason, note }: { state: string; reason?: string | null; note?: string | null }) {
  const { enums } = useSession();
  return <HealthDot spec={serverStateSpec(enums, state, reason, note)} />;
}

/**
 * D53: the state column of a table of servers, applications, projects or
 * clients — first, a dot only, the legend in the header. `get` gives the
 * row's state (null: not in production).
 */
export function serverStateColumn<T>(enums: Enums, get: (row: T) => { state: string | null; reason?: string | null; note?: string | null; retired?: string | null }, { noProduction = false, securityRow = false, key, title, abbreviation }: { noProduction?: boolean; securityRow?: boolean; key?: string; title?: string; abbreviation?: string } = {}) {
  return healthColumn<T>({
    key, title, abbreviation,
    legendLabel: title ? t('servers.legendOf', { column: title }) : t('servers.legend'),
    legendTitle: t('servers.legendTitle'),
    legend: <ServerStateLegend noProduction={noProduction} securityRow={securityRow} />,
    spec: row => {
      const s = get(row);
      // SPEC-034 § 12.4: a retired deployment (volumes kept, no server) says its installation state, not « Inconnu ».
      if (s.retired) return { tone: 'neutral', variant: 'ring', label: t(`environment.state.${s.retired}`) };
      return s.state === null ? noProductionSpec() : serverStateSpec(enums, s.state, s.reason, s.note);
    },
  });
}

/** « À mettre à jour » for security reasons: the state keeps its colour, the badge says it is security (SPEC-003). */
export function SecurityBadge() {
  return <Badge tone="neg" size="sm" icon="shield">{t('servers.security')}</Badge>;
}

/** A lone state (page header): a stamp. */
export function ServerStateBadge({ state, security }: { state: string; security?: boolean }) {
  const meta = useStateMeta(state);
  return security
    ? <Inline gap={8} align="center" wrap={false}><Badge tone={meta.tone}>{meta.label}</Badge><SecurityBadge /></Inline>
    : <Badge tone={meta.tone}>{meta.label}</Badge>;
}

/** The server states with the markers of the cells; `noProduction` adds « Pas en production » (inventory lists). */
export function ServerStateLegend({ noProduction = false, securityRow = false }: { noProduction?: boolean; securityRow?: boolean }) {
  const { enums } = useSession();
  const legend = <HealthLegend items={[...enums.states.map(s => serverStateSpec(enums, s.value)), ...(noProduction ? [noProductionSpec()] : [])]} />;
  // D63: the list tints the row of a server with a pending security update (DSM DataTable rowTone): said here too.
  return securityRow ? <Stack gap={12}>{legend}<Text variant="body-sm">{t('servers.securityRowLegend')}</Text></Stack> : legend;
}

/** D63: a pending security update tints the whole row (DSM rowTone), its words for screen readers. */
export const securityRowTone = (security: boolean) => (security ? 'neg' as const : undefined);
export const securityRowLabel = (security: boolean) => (security ? t('servers.securityRow') : '');

/**
 * D63: the environments of the deployments declared on a server, as short
 * tags (P, PP, R, D); the full type in the tooltip. Nothing when no
 * application is declared there yet. D74: in their own column « E ».
 */
export function EnvironmentTags({ types, spare = false }: { types: string[]; spare?: boolean }) {
  const { enums } = useSession();
  return (
    <Inline gap={4} align="center" wrap={false}>
      {/* D77: kept in reserve (it hosts nothing, so no environment tag beside it). */}
      {spare && <Badge tone="info" size="sm" title={t('spare.summary')}>{t('spare.badge')}</Badge>}
      {types.map(type => (
        <Badge key={type} tone="neutral" variant={type === 'production' ? 'solid' : 'ring'} size="sm" title={enums.environmentTypes.find(e => e.value === type)?.label ?? type}>
          {t(`servers.environmentInitial.${type}`)}
        </Badge>
      ))}
    </Inline>
  );
}

/** D74: the legend of the « E » column: each initial and its environment type. */
export function EnvironmentLegend() {
  const { enums } = useSession();
  return (
    <Stack gap={8}>
      {['production', 'preproduction', 'acceptance', 'development'].map(type => (
        <Inline key={type} gap={8} align="center" wrap={false}>
          <Badge tone="neutral" variant={type === 'production' ? 'solid' : 'ring'} size="sm">{t(`servers.environmentInitial.${type}`)}</Badge>
          <Text variant="body-sm">{enums.environmentTypes.find(e => e.value === type)?.label ?? type}</Text>
        </Inline>
      ))}
      <Inline gap={8} align="center" wrap={false}>
        <Badge tone="info" size="sm">{t('spare.badge')}</Badge>
        <Text variant="body-sm">{t('spare.summary')}</Text>
      </Inline>
      <Text variant="body-sm" tone="mute">{t('servers.environmentLegendNote')}</Text>
    </Stack>
  );
}

/**
 * Environment stamp: neutral tone, production solid (state tones stay reserved, ADR-003). D114: given `state`
 * (inventory lists), the stamp takes that state's tone and dot: the state of each environment of the row.
 * D48: a deployment's environment (`type` given) shows its name, its type sets the
 * variant; a server's environment shows its label and the type /enums gives it.
 */
export function EnvironmentBadge({ environment, type, unset = false, state }: { environment: string; type?: string | null; unset?: boolean; state?: string | null }) {
  const { enums } = useSession();
  // D23: no environment declared; treated as production until qualified.
  if (unset) return <Badge tone="warn" variant="ring" size="sm">{t('inventory.environmentUnset')}</Badge>;
  const server = enums.environments.find(e => e.value === environment);
  // The four names of before D48 read in full (prod → Production); a free name as written.
  const label = server?.label ?? environment;
  const variant = (type ?? server?.type) === 'production' ? 'solid' : 'ring';
  // D114: in the inventory lists, the environment says its own state (tone and dot, the state in its accessible name).
  if (state !== undefined) {
    const known = state ? enums.states.find(x => x.value === state) : undefined;
    return (
      <Tooltip content={known?.label ?? t('inventory.noState')}>
        <Badge tone={known?.tone ?? 'neutral'} variant={variant} dot size="sm" title={`${label} : ${known?.label ?? t('inventory.noState')}`}>{label}</Badge>
      </Tooltip>
    );
  }
  return <Badge tone="neutral" variant={variant} size="sm">{label}</Badge>;
}

/**
 * D58: in a table, never a badge after a text: the environment of a row has its
 * own column, the stamp alone so the stamps line up. Its name stays in
 * `accessor` for the search, the sort and the CSV.
 */
export function environmentColumn<T>(label: string, name: (environment: string) => string,
  get: (row: T) => { environment: string; type?: string | null } | null | undefined) {
  return {
    key: 'environment', label, width: 140,
    accessor: (row: T) => { const e = get(row); return e ? name(e.environment) : ''; },
    render: (row: T) => { const e = get(row); return e ? <EnvironmentBadge environment={e.environment} type={e.type} /> : null; },
  };
}


/**
 * RG-032-2: a declared address that no site of its servers serves. D58: said in
 * its own column after the address, no longer a badge after it.
 */
/**
 * A public address as a single icon (opens it in a new tab, the address in its tooltip and its name): the tables
 * that list deployments keep their width for the names. Nothing at all without a public address.
 */
/**
 * D109: where an application opens. Its entry points when the repository declares them (« Back-office » →
 * /admin), each a small link with its name; else its address as one icon.
 */
export function EntryLinks({ url, entryPoints }: { url?: string | null; entryPoints?: { label: string; url: string }[] | null }) {
  if (!entryPoints?.length) return <UrlButton url={url} />;
  return (
    <span onClick={e => e.stopPropagation()}>
      <Inline gap={4} wrap={false}>
        {entryPoints.map(p => (
          <Tooltip key={p.url} content={p.url.replace(/^https?:\/\//, '')} describe={false}>
            <Button variant="ghost" size="sm" trailingIcon="open_in_new" href={p.url} target="_blank" rel="noreferrer">{p.label}</Button>
          </Tooltip>
        ))}
      </Inline>
    </span>
  );
}

export function UrlButton({ url }: { url?: string | null }) {
  if (!url) return null;
  const shown = url.replace(/^https?:\/\//, '');
  return (
    // A row that opens on click must not open when the address is.
    <span onClick={e => e.stopPropagation()}>
      <Tooltip content={shown} describe={false}>
        <Button variant="ghost" size="sm" icon="open_in_new" href={url} target="_blank" rel="noreferrer" aria-label={t('server.deployments.open', { url: shown })} />
      </Tooltip>
    </span>
  );
}

export function notFoundColumn<T extends { url?: string | null; foundOnServer?: boolean | null }>() {
  const notFound = (d: T) => Boolean(d.url) && d.foundOnServer === false;
  return {
    key: 'foundOnServer', label: t('server.deployments.columns.found'), width: 200, sortable: false,
    accessor: (d: T) => (notFound(d) ? t('server.deployments.notFound') : ''),
    render: (d: T) => (notFound(d) ? <Badge tone="warn" size="sm">{t('server.deployments.notFound')}</Badge> : null),
  };
}

/**
 * Enrolment stage (« E » column, D37): a dot whose colour and shape tell the
 * stage, its name in the tooltip and in the column's legend. Pairs the DSM
 * calls unsafe by colour alone (pos/neg, info/solid neutral) differ by shape.
 */
export const ENROLMENT_DOT = {
  to_bootstrap: { tone: 'neutral', variant: 'ring' },
  to_enrol: { tone: 'warn', variant: 'solid' },
  enrolling: { tone: 'info', variant: 'halo' },
  enrolled: { tone: 'pos', variant: 'solid' },
  failed: { tone: 'neg', variant: 'halo' },
  not_applicable: { tone: 'neutral', variant: 'solid' },
} as const;
export type EnrolmentStage = keyof typeof ENROLMENT_DOT;
/** The legend's order: the path of a server, then the two side cases. */
const ENROLMENT_ORDER: EnrolmentStage[] = ['to_bootstrap', 'to_enrol', 'enrolling', 'enrolled', 'failed', 'not_applicable'];

export function EnrolmentDot({ stage }: { stage: EnrolmentStage }) {
  const dot = ENROLMENT_DOT[stage];
  return <StatusDot tone={dot.tone} variant={dot.variant} label={t(`servers.enrolment.${stage}`)} description={t(`servers.enrolmentHelp.${stage}`)} />;
}

export function EnrolmentLegend() {
  return (
    <Legend stacked label={t('servers.enrolmentLegendTitle')}
      items={ENROLMENT_ORDER.map(stage => ({ key: stage, ...ENROLMENT_DOT[stage], label: t(`servers.enrolment.${stage}`), description: t(`servers.enrolmentHelp.${stage}`) }))} />
  );
}
